import { createSelector } from '@reduxjs/toolkit';
import type {
  Memory,
  MemoryConstructionParams,
  MemoryGroup,
  MemoryLane,
} from '@timelyapp/integration-cloud-api-spec';
import { constructMemoryGroups } from '@timelyapp/integration-cloud-sdk/memory-processors/utils.memories';
import { constructMemoryGroupSummaries } from '@timelyapp/integration-cloud-sdk/memory-processors/utils.memories.summary';
import { constructMemoryLanes } from '@timelyapp/integration-cloud-sdk/memory-processors/utils.memory.lanes';
import { EMPTY_ARRAY } from 'constants/defaults';
import { isEqual, minBy } from 'lodash-es';
import { DateTime } from 'luxon';
import { getHasConnectedMemorySource } from 'redux/selectors/apps';
import { fromISO } from 'utils/luxon-helpers';
import { lazySelector } from 'utils/reselect';
import { findLastRelevantMemoryEndedAt } from 'utils/tic/tic-memory-last-ended-at';
import { getTicApiSlice } from './api-slice/tic.api.selector';
import { ticApi } from './api-slice/tic.apiSlice';
import {
  getTicMemoryDate,
  getTimelyLegacyMemoriesAsTicMemories,
} from './tic.legacy-memories.selectors';
import { getMemoryConfig } from './tic.memories.config.selectors';
import {
  enrichMemoryOrGroupWithTimeTrackingMeta,
  type MemoryOrGroupWithTimeTrackingMeta,
} from './tic.memories.selectors.utils';
import { getDemoMemoriesData } from './tic.memories.synthetic.selectors';
import { getToolConfigsMap } from './tools/tic.tool_configs.selectors';

/** Current YYYY-MM-DD date, and start/end of timeline */
export const getMemoryDateRange = createSelector([getTicMemoryDate], date => {
  const dt = fromISO(date);
  return {
    date,
    startDate: dt.startOf('day').minus({ hours: 1 }).toISO(),
    endDate: dt.endOf('day').plus({ hours: 1 }).toISO(),
  };
});

/** HTTP, maybe kill off after usage is clear */
export const getMemoryQueryParams = createSelector(
  [getMemoryDateRange],
  ({ startDate, endDate }) => {
    return { startDate, endDate, page: 1, limit: 2000 };
  }
);

/**
 * Returns the memory config, all tool configs, and date range.
 */
export const getMemoryConstructionParams = createSelector(
  [getMemoryConfig, getToolConfigsMap, getMemoryDateRange],
  (memoryConfig, toolConfigsMap, { startDate, endDate }): MemoryConstructionParams => {
    return {
      ...memoryConfig,
      toolConfigsMap,
      startDate,
      endDate,
    };
  }
);

export const getTicMemoriesRTKQueryBase = createSelector(
  [getTicApiSlice, getMemoryQueryParams],
  (localState, queryParams) =>
    ticApi.endpoints.getMemories.select(queryParams)({ [ticApi.reducerPath]: localState })
);

export const getTicMemories = createSelector([getTicMemoriesRTKQueryBase], ({ data }): Memory[] => {
  return (data?.memories || EMPTY_ARRAY) as Memory[];
});

/** Check if any TIC memories are loaded in state (and if no, we're in "legacy Memories" mode) */
export const getHasAnyTicMemoriesLoaded = createSelector([getTicMemories], m => !!m.length);

/** Check if user has any memory source - either legacy (desktop/browser) or TIC (integrations) */
export const getHasAnyMemorySource = createSelector(
  [getHasConnectedMemorySource, getHasAnyTicMemoriesLoaded],
  (hasLegacyMemorySource, hasTicMemories) => hasLegacyMemorySource || hasTicMemories
);

export const getTicAndLegacyMemories = createSelector(
  [getTicMemories, getTimelyLegacyMemoriesAsTicMemories, getDemoMemoriesData, getMemoryConfig],
  (ticMemories, legacyMemories, demoMemoriesData, memoryConfig): Memory[] => {
    // If we're showing dummy memories i.e. we're in demo mode,
    // we don't want to include any real memories.
    if (memoryConfig.demoSettings?.enabled) {
      return demoMemoriesData.entries;
    }
    return [...ticMemories, ...legacyMemories];
  }
);
/**
 * Returns the datetime of the most recently _ended_ memory,
 * used by AI trigger checks to determine if inference is needed.
 *
 * @note Any future ended_at memories are omitted, as they would cause
 *       continuous inference requests.
 */
export const getLastRelevantMemoryEndingForAiStatus = createSelector(
  [getTicAndLegacyMemories],
  memories => findLastRelevantMemoryEndedAt(memories, { excludeFuture: true })
);

export const getFirstMemoryStartedAt = createSelector(
  [getTicAndLegacyMemories],
  memories => minBy(memories, m => DateTime.fromISO(m.started_at))?.started_at ?? null
);
/**
 * Return the datetime of the last memory.ended_at
 * @warning Includes those that end in the future (e.g, calendar events)
 */
export const getLastMemoryEndedAt = createSelector([getTicAndLegacyMemories], memories =>
  findLastRelevantMemoryEndedAt(memories, { excludeFuture: false })
);

const getTimestampedHoursAndDraftForCurrentDay = lazySelector(
  import('../../selectors/hours.timestamps.base.selector').then(
    m => m.getTimestampedHoursAndDraftForCurrentDay
  )
);
/**
 * Combines Timely's memories in TIC format + TIC memories, and produces MemoryGroups based on user's query (view preference).
 * @link https://docs.integrations.timelyapp.com/api-1.0/openapi#getmemories
 */
export const getConstructedTicAndLegacyMemoriesAsMemoryGroup = createSelector(
  [getTicAndLegacyMemories, getMemoryConstructionParams],
  (memories, query): MemoryGroup[] => {
    return constructMemoryGroups(memories, query);
  }
);

/**
 *
 * All of TIC & Timely Classic memories as groups,
 * PLUS `meta` of time tracking/logging relationships
 * directly (as in, checked) and indirectly (as in, overlapping with a timestamp)
 *
 * @warning This may be too heavy?
 */
export const getTicAndLegacyMemoriesAsMemoryGroups = createSelector(
  [getConstructedTicAndLegacyMemoriesAsMemoryGroup, getTimestampedHoursAndDraftForCurrentDay],
  (memoryGroups, hourCards): MemoryOrGroupWithTimeTrackingMeta<MemoryGroup>[] => {
    return memoryGroups.map(group => {
      return enrichMemoryOrGroupWithTimeTrackingMeta(group, hourCards ?? EMPTY_ARRAY);
    });
  },
  { memoizeOptions: { resultEqualityCheck: isEqual } }
);

/**
 * Constructs Memory Lanes based on combined TIC & Timely Memories.
 *
 * @todo Sort lanes in order of duration.
 */
/**
 * Constructs the summary memory blocks for the timeline summary lane
 */
export const getTicMemorySummaryBlocks = createSelector(
  [getTicAndLegacyMemoriesAsMemoryGroups, getMemoryConstructionParams],
  (memoryGroups, params) => {
    return constructMemoryGroupSummaries(memoryGroups, params);
  }
);

export const getTicMemoriesInLanes = createSelector(
  [getTicAndLegacyMemoriesAsMemoryGroups, getMemoryConstructionParams],
  (memoryGroups, params) => {
    return constructMemoryLanes(memoryGroups, params).reduce(
      (acc, lane) => {
        if (lane.sticky) {
          acc.stickyLanes.push(lane);
        } else {
          acc.lanes.push(lane);
        }
        return acc;
      },
      { stickyLanes: [], lanes: [] } as { stickyLanes: MemoryLane[]; lanes: MemoryLane[] }
    );
  }
);

/**
 * Extracts unique tools from memory groups
 * This is used to suggest tools that don't already have tool configs
 *
 * @todo Use it (ref "lanes" dropdown) or kill it
 */
export const getUniqueToolsFromMemories = createSelector(
  [getTicAndLegacyMemories],
  (memories): Array<{ id?: string; raw?: string; title?: string; icon_url?: string }> => {
    const uniqueTools = new Map<
      string,
      { id?: string; raw?: string; title?: string; icon_url?: string }
    >();

    memories.forEach(memory => {
      if (memory.tool) {
        const toolId = memory.tool.id || memory.tool.raw;
        if (toolId && !uniqueTools.has(toolId)) {
          uniqueTools.set(toolId, {
            id: memory.tool.id || undefined,
            raw: memory.tool.raw || undefined,
            title: memory.tool.title,
            icon_url: memory.tool.icon_url || undefined,
          });
        }
      }
    });

    return Array.from(uniqueTools.values());
  }
);
